import { action, computed, observable } from 'mobx'
import { type IValidationSchema, required, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import { when } from '@/utils/validx-util'
import { ConfigEditorViewModelBase } from './ConfigEditorViewModelBase'
import type { PrepAndReviewConfig } from './legend-options'

/**
 * Splits tax jobs into a prep job and a review job, priced by service items.
 */
export class PrepAndReviewConfigEditorViewModel extends ConfigEditorViewModelBase<PrepAndReviewConfig> {
  private readonly _schema: IValidationSchema<PrepAndReviewConfigEditorViewModel> =
    {
      // Listed so that turning the flow on or off revalidates the items.
      enabled: [],
      prepServiceItemId: [
        when(() => this.enabled, required('Prep service item is required')),
      ],
      reviewServiceItemId: [
        when(() => this.enabled, required('Review service item is required')),
      ],
      prepAndReviewServiceItemId: [
        when(
          () => this.enabled,
          required('Prep and review service item is required'),
        ),
      ],
    }

  @observable accessor enabled = false
  @observable accessor previousPro = false
  @observable accessor prepServiceItemId = ''
  @observable accessor reviewServiceItemId = ''
  @observable accessor prepAndReviewServiceItemId = ''

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies) {
    super(dependencies)
    this.lifecycle.manage(
      validateWhileEditing(this, this._schema, {
        enabled: [
          'prepServiceItemId',
          'reviewServiceItemId',
          'prepAndReviewServiceItemId',
        ],
      }),
    )
  }

  /**
   * The service items the legend's service type can use, as options to pick
   * from.
   */
  @computed
  get availableInventoryItems() {
    return this.dependencies.catalog.allowedInventoryItems.flatMap((i) =>
      i.id ? [{ id: i.id, text: `${i.name} - ${i.shortId}` }] : [],
    )
  }

  /**
   * Turns the prep and review flow on or off.
   */
  @action.bound
  setEnabled(value: boolean) {
    this.enabled = value
  }

  /**
   * Whether the review job goes to the pro who did the prep.
   */
  @action.bound
  setPreviousPro(value: boolean) {
    this.previousPro = value
  }

  /**
   * Sets the service item that marks a prep job.
   */
  @action.bound
  setPrepServiceItemId(value: string) {
    this.prepServiceItemId = value
  }

  /**
   * Sets the service item that marks a review job.
   */
  @action.bound
  setReviewServiceItemId(value: string) {
    this.reviewServiceItemId = value
  }

  /**
   * Sets the service item that marks a job needing both prep and review.
   */
  @action.bound
  setPrepAndReviewServiceItemId(value: string) {
    this.prepAndReviewServiceItemId = value
  }

  /** @inheritDoc */
  @action
  hydrate(config: PrepAndReviewConfig | undefined) {
    this.enabled = config?.enabled ?? false
    this.previousPro = config?.previousPro ?? false
    this.prepServiceItemId = config?.prepServiceItemId ?? ''
    this.reviewServiceItemId = config?.reviewServiceItemId ?? ''
    this.prepAndReviewServiceItemId = config?.prepAndReviewServiceItemId ?? ''
  }

  /** @inheritDoc */
  toJSON(): PrepAndReviewConfig {
    return {
      enabled: this.enabled,
      previousPro: this.previousPro,
      prepServiceItemId: this.prepServiceItemId,
      reviewServiceItemId: this.reviewServiceItemId,
      prepAndReviewServiceItemId: this.prepAndReviewServiceItemId,
    }
  }
}
